import React, { useEffect, useState } from "react";
import { Check, ChevronDown, Loader2, X } from "lucide-react";
import {
  formatElapsed,
  formatStepDuration,
  summarizeWork,
  activityLine,
} from "../../helpers/copilotSteps";
import "./CopilotWorkPanel.scss";

const TICK_MS = 500;

// `serverElapsedMs` is the last server-authoritative elapsed time this turn
// reported (the `heartbeat` event's own measurement, see HelpChatPanel's
// event dispatch). Used as a floor rather than as the whole answer: the
// client tick below is the smooth one, but requestAnimationFrame-adjacent
// timers are throttled in a backgrounded tab and setInterval itself drifts,
// so the clock can read low. The heartbeat is measured upstream of the
// browser and cannot drift with it, so whichever is larger is the honest one.
const useElapsed = (running, startedAt, serverElapsedMs) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!running || !startedAt) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [running, startedAt]);

  if (!running || !startedAt) return 0;
  const ticked = Math.max(0, now - startedAt);
  const reported =
    typeof serverElapsedMs === "number" &&
    Number.isFinite(serverElapsedMs) &&
    serverElapsedMs >= 0
      ? serverElapsedMs
      : 0;
  return Math.max(ticked, reported);
};

const StepIcon = ({ status }) => {
  if (status === "start") {
    return (
      <Loader2
        size={14}
        className="copilot-work__icon copilot-work__icon--spin"
        aria-hidden="true"
      />
    );
  }
  if (status === "error") {
    return <X size={14} className="copilot-work__icon" aria-hidden="true" />;
  }
  return <Check size={14} className="copilot-work__icon" aria-hidden="true" />;
};

const StepRow = ({ step }) => {
  const duration = formatStepDuration(step.ms);
  return (
    <li className={`copilot-work__step copilot-work__step--${step.status}`}>
      <StepIcon status={step.status} />
      <span className="copilot-work__step-text">
        <span className="copilot-work__label">{step.label}</span>
        {step.detail && (
          <span className="copilot-work__detail">{step.detail}</span>
        )}
        {step.result && (
          <span className="copilot-work__result">{step.result}</span>
        )}
      </span>
      {duration && <span className="copilot-work__ms">{duration}</span>}
    </li>
  );
};

const CopilotWorkPanel = ({
  steps,
  running,
  startedAt,
  totalMs,
  serverElapsedMs,
}) => {
  const [expanded, setExpanded] = useState(false);
  const elapsed = useElapsed(running, startedAt, serverElapsedMs);
  const list = Array.isArray(steps) ? steps : [];

  // A running turn renders its header (and the clock) from the moment the
  // turn starts, before any step exists - the first tool call can be seconds
  // away, and self-suppressing until then left the whole wait showing a
  // static label with no elapsed time at all. A settled turn with no steps
  // still renders nothing: there is no work to summarize.
  if (list.length === 0 && !running) return null;

  const line = activityLine(list, elapsed);

  if (running) {
    return (
      <div className="copilot-work copilot-work--running">
        <div className="copilot-work__header">
          <Loader2
            size={14}
            className="copilot-work__icon copilot-work__icon--spin"
            aria-hidden="true"
          />
          {/* Deliberately a plain span, never a live region: this text
              changes every couple of seconds, and the panel's one polite
              announcement for a streaming turn lives in HelpChatPanel's own
              status region, which holds a constant string for exactly that
              reason. Keyed so React swaps the node and the fade replays on
              each new line rather than the text snapping in place. */}
          <span
            key={line.key}
            className="copilot-work__heading copilot-work__heading--fade"
          >
            {line.text}
          </span>
          <span className="copilot-work__timer">{formatElapsed(elapsed)}</span>
        </div>
        {list.length > 0 && (
          <ul className="copilot-work__steps">
            {list.map((step) => (
              <StepRow key={step.id} step={step} />
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="copilot-work">
      <button
        type="button"
        className="copilot-work__summary"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
      >
        <ChevronDown
          size={14}
          className={`copilot-work__chevron${expanded ? " is-open" : ""}`}
          aria-hidden="true"
        />
        {summarizeWork(list, totalMs)}
      </button>
      {expanded && (
        <ul className="copilot-work__steps">
          {list.map((step) => (
            <StepRow key={step.id} step={step} />
          ))}
        </ul>
      )}
    </div>
  );
};

export default CopilotWorkPanel;
